import { observer } from "mobx-react-lite";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { Layers3, LayoutGrid } from "lucide-react";
import { useTran } from "@/hooks";
import PillTabs from "@/components/common/PillTabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatValue } from "../../../domains/formatting";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { dimensionLabels } from "../../../utils/e-charts-dimensions";
import {
  buildDrillControlCopy,
  buildEmptyStateCopy,
  buildHeroDescriptionCopy,
  buildHeroTitleCopy,
  DRILL_HINT_COPY,
  EMPTY_CAUSE,
  resolveInteractionState,
} from "../../../utils/e-charts-view";
import { renderHeroBarSeries } from "./hero-bar-series";
import SortedTooltipContent from "./SortedTooltipContent";
import { stableAxisKey, stableAxisMax } from "./stable-y-axis";
import { buildSortedTooltipRows } from "./tooltip-rows";

const HIDDEN_TOOLTIP_WRAPPER_STYLE = {
  display: "none",
};
const TOOLTIP_OFFSET = 10;
const TOOLTIP_VIEWPORT_PADDING = 8;

const TOTAL_KEY = "total";
const CHART_HEIGHT = 320;
const YEAR_WIDTH = 280;
const Y_AXIS_WIDTH = 72;
const X_AXIS_HEIGHT = 30;
const AXIS_CHART_MARGIN = { top: 5, right: 0, bottom: 5, left: 5 };
const PLOT_CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: 0 };

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const getChartWidth = (yearCount) => Math.max(yearCount, 1) * YEAR_WIDTH;

const EChartsHeroPortalTooltip = ({
  active,
  coordinate,
  label,
  payload,
  chartHostRef,
  consolidated,
  entityNameById,
  hintLabel,
  // The tooltip portals to document.body, so the stale-view veil's opacity
  // and inert cannot reach it; without this flag a tooltip left open when a
  // refetch starts (pointer parked on a bar) would float full-strength above
  // the dimmed chart until the pointer moves.
  veiled,
  t,
}) => {
  const tooltipRef = useRef(null);
  const [hostRect, setHostRect] = useState(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  // Rows sorted biggest first with each entity's share of the displayed
  // total (R09 item 4, matching the client-facing "250 (25%)" format).
  // Consolidated mode keeps one Total line; a percentage would always be 100%.
  const { rows, totalLabel } = useMemo(() => {
    if (consolidated) {
      const item = (payload ?? [])[0];
      if (!item) return { rows: [], totalLabel: null };
      return {
        rows: [
          {
            key: TOTAL_KEY,
            name: t("Total"),
            color: item.color || item.fill,
            valueLabel: formatValue(Number(item.value)),
          },
        ],
        totalLabel: null,
      };
    }
    const sorted = buildSortedTooltipRows(payload, (item) => {
      const id = String(item.dataKey ?? item.name);
      return entityNameById.get(id) ?? id;
    });
    return {
      rows: sorted.rows,
      totalLabel: `${t("Total")}: ${formatValue(sorted.sum)}`,
    };
  }, [consolidated, entityNameById, payload, t]);

  useLayoutEffect(() => {
    if (!active || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    const nextSize = {
      width: Math.ceil(rect.width),
      height: Math.ceil(rect.height),
    };
    setTooltipSize((prev) =>
      prev.width === nextSize.width && prev.height === nextSize.height
        ? prev
        : nextSize,
    );
    // hostRect: the portal first mounts on the render hostRect resolves, so
    // the measure must re-run then or the first paint skips viewport clamping.
  }, [active, label, rows, hostRect]);

  useLayoutEffect(() => {
    if (!active) return;

    const updateHostRect = () => {
      const nextRect = chartHostRef.current?.getBoundingClientRect();
      if (!nextRect) {
        setHostRect(null);
        return;
      }
      const nextValue = {
        left: nextRect.left,
        top: nextRect.top,
        width: nextRect.width,
        height: nextRect.height,
      };
      setHostRect((prev) =>
        prev &&
        prev.left === nextValue.left &&
        prev.top === nextValue.top &&
        prev.width === nextValue.width &&
        prev.height === nextValue.height
          ? prev
          : nextValue,
      );
    };

    updateHostRect();
    window.addEventListener("resize", updateHostRect);
    window.addEventListener("scroll", updateHostRect, true);

    return () => {
      window.removeEventListener("resize", updateHostRect);
      window.removeEventListener("scroll", updateHostRect, true);
    };
  }, [active, chartHostRef, coordinate]);

  if (
    typeof document === "undefined" ||
    veiled ||
    !active ||
    !coordinate ||
    rows.length === 0 ||
    !hostRect
  ) {
    return null;
  }

  const rawLeft = hostRect.left + coordinate.x + TOOLTIP_OFFSET;
  const rawTop = hostRect.top + coordinate.y + TOOLTIP_OFFSET;
  const maxLeft =
    window.innerWidth - tooltipSize.width - TOOLTIP_VIEWPORT_PADDING;
  const maxTop =
    window.innerHeight - tooltipSize.height - TOOLTIP_VIEWPORT_PADDING;
  const left =
    tooltipSize.width > 0
      ? clamp(
          rawLeft,
          TOOLTIP_VIEWPORT_PADDING,
          Math.max(TOOLTIP_VIEWPORT_PADDING, maxLeft),
        )
      : rawLeft;
  const top =
    tooltipSize.height > 0
      ? clamp(
          rawTop,
          TOOLTIP_VIEWPORT_PADDING,
          Math.max(TOOLTIP_VIEWPORT_PADDING, maxTop),
        )
      : rawTop;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 30,
        // Never a hit-test target: a box that overlaps the cursor would steal
        // the pointer from the chart and flicker the tooltip on/off. The box
        // no longer scrolls routinely, so it does not need pointer events.
        pointerEvents: "none",
      }}
    >
      <SortedTooltipContent
        label={label}
        totalLabel={totalLabel}
        rows={rows}
        rankTop={!consolidated}
        footerLabel={hintLabel}
      />
    </div>,
    document.body,
  );
};

const EChartsHero = observer(
  ({
    entities,
    years,
    entityNameById,
    entityColorById,
    displayedDimension,
    axisContextKey,
    // From the panel, not the store: while a refetch is pending this is the
    // drill of the view the ROWS answer, so the title, description, and
    // toggle visibility swap together with the data, never ahead of it.
    drillContext,
    refreshing,
    emptyState,
    onEmptyStateAction,
  }) => {
    const { t } = useTran();
    const store = dashboardEChartsStore;
    const consolidated = store.consolidated;
    const chartHostRef = useRef(null);
    const chartViewportRef = useRef(null);
    const [chartViewportWidth, setChartViewportWidth] = useState(0);

    // Coerce missing cells to 0 so recharts renders a zero-height bar AND
    // includes the entity in the tooltip ("Offshore: 0"). Distinguishes
    // visually-nothing from absent-from-tooltip.
    const data = years.map((year) => {
      const row = { year };
      if (consolidated) {
        row[TOTAL_KEY] = store.consolidatedTotalFor(year, entities) ?? 0;
      } else {
        entities.forEach((id) => {
          row[String(id)] = store.totalFor(id, year) ?? 0;
        });
      }
      return row;
    });

    // Pinned Y scale (stable-y-axis.js): ticking, unticking, and drilling
    // must never move the axis, so the domain comes from the high-water mark
    // of this view, not from the currently displayed subset. All three
    // BarCharts below (axis rail, background grid, plot) MUST share this
    // domain — the plot charts hide their axes, so a mismatch would silently
    // draw bars on a different scale than the visible rail.
    const dataMax = data.reduce((max, row) => {
      const values = consolidated
        ? [row[TOTAL_KEY]]
        : entities.map((id) => row[String(id)]);
      return values.reduce(
        (m, v) => (Number.isFinite(v) && v > m ? v : m),
        max,
      );
    }, 0);
    const yAxisMax = stableAxisMax(
      stableAxisKey({
        contextKey: axisContextKey,
        chartId: "hero",
        dimension: displayedDimension,
        consolidated,
      }),
      dataMax,
    );
    const yAxisDomain = yAxisMax == null ? undefined : [0, yAxisMax];

    const entityCount = entities.length;
    const canConsolidate = entityCount >= 2;
    const titleCopy = buildHeroTitleCopy({
      displayedDimension,
      drillContext,
      consolidated,
    });
    const descriptionCopy = buildHeroDescriptionCopy({
      displayedDimension,
      drillContext,
      consolidated,
      entityCount,
    });
    const headerTitle = t(titleCopy.key, titleCopy.params);
    const headerDesc = t(descriptionCopy.key, descriptionCopy.params);

    // Hero-only trigger, per-entity view only, one level only. Sub-chart bars
    // and already-drilled bars get no handler and no affordance.
    const { canDrill } = resolveInteractionState({
      consolidated,
      drillContext,
    });
    const handleDrill = (id) => {
      store.drillInto(id, entityNameById.get(String(id)) ?? String(id));
    };

    const isEmpty = emptyState != null;
    const emptyCopy = isEmpty
      ? buildEmptyStateCopy({
          cause: emptyState.cause,
          displayedDimension,
          drillContext,
          overLimitDimension: emptyState.overLimitDimension ?? null,
          noneSelectedDimension: emptyState.dimension ?? null,
        })
      : null;
    const chartWidth = getChartWidth(years.length);
    const isOverflowing =
      chartViewportWidth > 0 && chartWidth > chartViewportWidth;
    const plotCanvasWidth =
      chartViewportWidth > 0
        ? isOverflowing
          ? chartWidth
          : chartViewportWidth
        : chartWidth;
    const plotOffset = isOverflowing
      ? 0
      : Math.max(0, (plotCanvasWidth - chartWidth) / 2);

    const labels = dimensionLabels(displayedDimension);
    const perLabel = t(labels.perView);
    const allLabel = t(labels.all);
    const viewTabs = [
      { value: "per-entity", label: perLabel, icon: LayoutGrid },
      { value: "consolidated", label: allLabel, icon: Layers3 },
    ];
    const viewValue = consolidated ? "consolidated" : "per-entity";
    const handleViewChange = (next) =>
      store.setConsolidated(next === "consolidated");

    useLayoutEffect(() => {
      const node = chartViewportRef.current;
      if (!node) return;

      const updateViewportWidth = () => {
        const nextWidth = Math.floor(node.clientWidth || 0);
        setChartViewportWidth((prev) =>
          prev === nextWidth ? prev : nextWidth,
        );
      };

      updateViewportWidth();

      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(() => updateViewportWidth());
        observer.observe(node);
        return () => observer.disconnect();
      }

      window.addEventListener("resize", updateViewportWidth);
      return () => window.removeEventListener("resize", updateViewportWidth);
      // isEmpty: the viewport node only exists in the non-empty render, and
      // first mount is usually empty (org tree still loading), so an
      // empty-keyed rerun is what actually attaches the measurement.
    }, [isEmpty]);

    return (
      <Card className="flex w-full flex-col rounded-xl border-border/70 bg-card/95 p-0 shadow-sm">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 border-b px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-bold text-foreground">
              {headerTitle}{" "}
              <span className="text-xs font-normal text-muted-foreground">
                tCO₂e
              </span>
            </CardTitle>
            <CardDescription className="text-xs font-normal text-muted-foreground">
              {headerDesc}
            </CardDescription>
          </div>
          {/* Hidden during a drill: from inside a drill the pill would both
              consolidate AND exit, which reads as a mislabeled navigation
              (Codex round 3 finding 4). Shown whenever consolidated is on,
              even with one entity, so the view can never wedge in a mode
              whose only exit control has disappeared. `invisible` rather than
              unmounted while drilled, so the header keeps its exact size and
              entering or leaving a drill moves nothing (standing no-jump
              rule); visibility:hidden also removes it from tab order and the
              accessibility tree. */}
          {(canConsolidate || consolidated) && (
            <div className={`shrink-0 ${drillContext ? "invisible" : ""}`}>
              <PillTabs
                tabs={viewTabs}
                value={viewValue}
                onChange={handleViewChange}
                size="sm"
              />
            </div>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {isEmpty ? (
            emptyState.cause === EMPTY_CAUSE.NOT_LOADED ? (
              // First load, with nothing previous to keep on screen: a
              // skeleton frame at the chart's own height, so the loaded chart
              // replaces it without moving anything (brief section 3 point 4).
              <div
                className="flex animate-pulse items-center justify-center rounded-md bg-muted/40 text-xs text-muted-foreground"
                style={{ minHeight: `${CHART_HEIGHT}px` }}
              >
                <p>{t(emptyCopy.message.key, emptyCopy.message.params)}</p>
              </div>
            ) : (
              // One sentence naming the cause, with the way out inline. The
              // five causes never share a message: telling a user to change
              // the year when the real problem is a stale country selection
              // sends them the wrong way (brief 3.2 item 12). Same height as
              // the chart, so resolving to or from an empty view never
              // reflows the page (brief section 3 point 5).
              <div
                className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/70 p-8 text-center text-xs text-muted-foreground"
                style={{ minHeight: `${CHART_HEIGHT}px` }}
              >
                <p>{t(emptyCopy.message.key, emptyCopy.message.params)}</p>
                {emptyCopy.action ? (
                  <button
                    type="button"
                    onClick={() => onEmptyStateAction?.(emptyCopy.action.kind)}
                    className="rounded-sm px-1 py-0.5 font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t(emptyCopy.action.label.key)}
                  </button>
                ) : null}
              </div>
            )
          ) : (
            <div className="flex items-stretch">
              <div className="shrink-0" style={{ width: `${Y_AXIS_WIDTH}px` }}>
                <BarChart
                  width={Y_AXIS_WIDTH}
                  height={CHART_HEIGHT}
                  data={data}
                  barGap={4}
                  margin={AXIS_CHART_MARGIN}
                >
                  <XAxis
                    dataKey="year"
                    hide
                    height={X_AXIS_HEIGHT}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    width={Y_AXIS_WIDTH}
                    domain={yAxisDomain}
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  {renderHeroBarSeries({
                    consolidated,
                    entities,
                    entityColorById,
                    hidden: true,
                  })}
                </BarChart>
              </div>

              <div
                ref={chartViewportRef}
                className="min-w-0 flex-1 overflow-x-auto"
              >
                <div
                  className="relative min-w-fit shrink-0"
                  style={{
                    width: `${plotCanvasWidth}px`,
                    minWidth: `${plotCanvasWidth}px`,
                    height: `${CHART_HEIGHT}px`,
                  }}
                >
                  <div className="absolute inset-0">
                    <BarChart
                      width={plotCanvasWidth}
                      height={CHART_HEIGHT}
                      data={data}
                      barGap={4}
                      margin={PLOT_CHART_MARGIN}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="year"
                        hide
                        height={X_AXIS_HEIGHT}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis hide width={0} domain={yAxisDomain} />
                      {renderHeroBarSeries({
                        consolidated,
                        entities,
                        entityColorById,
                        hidden: true,
                      })}
                    </BarChart>
                  </div>

                  <div
                    className="absolute inset-y-0"
                    style={{ left: `${plotOffset}px` }}
                  >
                    <div
                      ref={chartHostRef}
                      className="min-w-fit shrink-0"
                      style={{
                        width: `${chartWidth}px`,
                        minWidth: `${chartWidth}px`,
                      }}
                    >
                      <BarChart
                        width={chartWidth}
                        height={CHART_HEIGHT}
                        data={data}
                        barGap={4}
                        margin={PLOT_CHART_MARGIN}
                      >
                        <XAxis
                          dataKey="year"
                          tick={{
                            fontSize: 12,
                            fill: "hsl(var(--muted-foreground))",
                          }}
                          height={X_AXIS_HEIGHT}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis hide width={0} domain={yAxisDomain} />
                        <Tooltip
                          content={
                            <EChartsHeroPortalTooltip
                              chartHostRef={chartHostRef}
                              consolidated={consolidated}
                              entityNameById={entityNameById}
                              hintLabel={
                                canDrill ? t(DRILL_HINT_COPY.key) : null
                              }
                              veiled={refreshing}
                              t={t}
                            />
                          }
                          cursor={{ fill: "hsl(var(--muted))" }}
                          wrapperStyle={HIDDEN_TOOLTIP_WRAPPER_STYLE}
                        />
                        {renderHeroBarSeries({
                          consolidated,
                          entities,
                          entityColorById,
                          onDrill: canDrill ? handleDrill : null,
                        })}
                      </BarChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* SVG bars are pointer-only, so the clickable state also publishes
              one real button per entity. Visually hidden, keyboard and screen
              reader reachable (brief 3.2 item 14). */}
          {!isEmpty && canDrill ? (
            <div className="sr-only">
              <ul aria-label={t("Drill into an entity")}>
                {entities.map((id) => {
                  const controlCopy = buildDrillControlCopy(
                    entityNameById.get(String(id)) ?? String(id),
                  );
                  return (
                    <li key={String(id)}>
                      <button
                        type="button"
                        onClick={() => handleDrill(String(id))}
                      >
                        {t(controlCopy.key, controlCopy.params)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  },
);

export default EChartsHero;
